/**
 * User Table Component
 * Display user list with actions
 */

import type { UserListItem } from '@hooks/useAdminUsers';
import { CheckCircle, Edit, ShieldCheck, XCircle } from 'lucide-react';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface UserTableProps {
  users: UserListItem[];
  onViewUser: (userId: string) => void;
}

// =============================================================================
// Component
// =============================================================================

export function UserTable({ users, onViewUser }: UserTableProps): ReactElement {
  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/20 text-red-400';
      case 'elite':
        return 'bg-indigo-500/20 text-indigo-400';
      case 'premium':
        return 'bg-purple-500/20 text-purple-400';
      case 'pro':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-theme-border">
            <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
              User
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
              Email
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
              Role
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
              Status
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
              Joined
            </th>
            <th className="text-right py-3 px-4 text-sm font-medium text-theme-text-secondary">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center py-8 text-theme-text-secondary">
                No users found
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <tr
                key={user.id}
                className="border-b border-theme-border hover:bg-theme-bg-secondary transition-colors"
              >
                {/* User */}
                <td className="py-3 px-4">
                  <div className="flex flex-col">
                    <span className="font-medium text-theme-text">{user.displayName}</span>
                    <span className="text-sm text-theme-text-tertiary">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                </td>

                {/* Email */}
                <td className="py-3 px-4">
                  <span className="text-sm text-theme-text">{user.email}</span>
                </td>

                {/* Role */}
                <td className="py-3 px-4">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}
                  >
                    {user.role === 'admin' && <ShieldCheck className="w-3 h-3" />}
                    <span className="capitalize">{user.role}</span>
                  </span>
                </td>

                {/* Status */}
                <td className="py-3 px-4">
                  <span
                    className={`inline-flex items-center gap-1 text-sm ${
                      user.emailVerified ? 'text-theme-success' : 'text-theme-text-tertiary'
                    }`}
                  >
                    {user.emailVerified ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4" />
                        <span>Unverified</span>
                      </>
                    )}
                  </span>
                </td>

                {/* Joined */}
                <td className="py-3 px-4 text-sm text-theme-text-secondary">
                  {formatDate(user.createdAt)}
                </td>

                {/* Actions */}
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onViewUser(user.id)}
                      className="p-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-bg-secondary rounded-lg transition-colors"
                      title="View details"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
